import React, { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PageLayout from "../components/pageLayout";
import HtmlContent from "../components/HtmlContent";
import { useSiteData } from "../lib/siteApi";
import { groupProjectsByCountry, prettyCountry, projectBody } from "../lib/catalog";
import { projectHeadline, projectPhoto } from "../lib/projectPhotos";
import { ProjectExtras } from "../components/extraBlocks";

function ProjectGrid({ items }) {
  if (!items.length) return <p className="renerg-body text-slate-500">No projects in this view yet.</p>;
  return (
    <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((item) => {
        const photo = projectPhoto(item);
        const title = projectHeadline(item);
        return (
        <Link key={item.id} to={`/projects/${item.id}`} className="group overflow-hidden rounded-3xl bg-white shadow-md transition hover:-translate-y-1 hover:shadow-xl">
          {photo ? (
            <img src={photo} alt={title} className="h-52 w-full object-cover transition duration-500 group-hover:scale-105" />
          ) : (
            <div className="h-40 bg-gradient-to-r from-teal-700 to-emerald-600" />
          )}
          <div className="p-5">
            <p className="project-kicker text-teal-700">{[prettyCountry(item.country), item.location, item.capacity].filter(Boolean).join(" · ")}</p>
            <h2 className="project-card-title mt-2 text-slate-900">{title}</h2>
            <span className="mt-3 inline-block text-sm font-semibold text-teal-700">View project →</span>
          </div>
        </Link>
        );
      })}
    </div>
  );
}

export default function ProjectsIndex() {
  const site = useSiteData();
  const all = useMemo(() => site.projects || [], [site.projects]);
  const grouped = groupProjectsByCountry(all);
  const countries = Object.keys(grouped);
  const [country, setCountry] = useState("all");
  const items = useMemo(
    () => (country === "all" ? all : all.filter((p) => prettyCountry(p.country) === country)),
    [all, country]
  );

  return (
    <PageLayout
      title="Projects"
      summary="Photographs of the plants first — the same project pictures as on renerg.co.rw. Use the country row to view all work, or only Rwanda, Kenya, Tchad and any market we publish."
      compact
      roomy
    >
      <div className="mb-8 flex gap-2 overflow-x-auto pb-2">
        <button type="button" className={`country-chip ${country === "all" ? "is-on" : ""}`} onClick={() => setCountry("all")}>All countries</button>
        {countries.map((name) => (
          <button type="button" key={name} className={`country-chip ${country === name ? "is-on" : ""}`} onClick={() => setCountry(name)}>
            {name}
          </button>
        ))}
      </div>
      <ProjectGrid items={items} />
    </PageLayout>
  );
}

export function ProjectCountryPage() {
  const { country } = useParams();
  const name = prettyCountry(decodeURIComponent(country || ""));
  const site = useSiteData();
  const items = (site.projects || []).filter((p) => prettyCountry(p.country) === name);

  return (
    <PageLayout
      eyebrow="Projects"
      title={`Projects in ${name}`}
      summary={`Published work in ${name}.`}
      compact
    >
      <ProjectGrid items={items} />
    </PageLayout>
  );
}

export function ProjectDetailPage() {
  const { id } = useParams();
  const site = useSiteData();
  const item = (site.projects || []).find((p) => p.id === id) || null;
  const country = prettyCountry(item?.country);
  const grouped = groupProjectsByCountry(site.projects || []);
  const siblings = grouped[country] || [];
  const related = siblings.filter((p) => p.id !== id).slice(0, 3).map((p) => ({
    to: `/projects/${p.id}`,
    title: projectHeadline(p),
    image: projectPhoto(p),
    kicker: [prettyCountry(p.country), p.location].filter(Boolean).join(" · "),
  }));

  return (
    <PageLayout
      eyebrow={[item?.country, item?.location, item?.capacity].filter(Boolean).join(" · ") || "Project"}
      title={item ? projectHeadline(item) : "Project"}
      image={item ? projectPhoto(item) : ""}
      compact
      related={related}
    >
      {!item ? (
        <p className="text-slate-500">Project not found.</p>
      ) : (
        <article className="max-w-4xl rounded-3xl bg-white p-8 shadow-lg lg:p-12">
          <div className="mb-8 grid gap-4 sm:grid-cols-3">
            <div className="rounded-2xl bg-teal-50 p-4"><p className="text-xs uppercase text-teal-700">Country</p><p className="font-semibold">{prettyCountry(item.country)}</p></div>
            <div className="rounded-2xl bg-teal-50 p-4"><p className="text-xs uppercase text-teal-700">Location</p><p className="font-semibold">{item.location || "—"}</p></div>
            <div className="rounded-2xl bg-teal-50 p-4"><p className="text-xs uppercase text-teal-700">Capacity</p><p className="font-semibold">{item.capacity || "—"}</p></div>
          </div>
          <div className="renerg-body text-slate-600">
            <HtmlContent html={projectBody(item)} imageTo={`/projects/${item.id}`} />
          </div>
          <ProjectExtras country={item.country} title={item.title} capacity={item.capacity} location={item.location} status={item.status} />
        </article>
      )}
    </PageLayout>
  );
}
